import type {
  CamposDeIntegracao,
  OrigemDoDado,
  SituacaoDaPessoa,
} from '@/modulos/operacao/comum/contrato';
import type { PermissoesDeLeitura } from '@/modulos/operacao/cadastros/leitura';
import type { NaturezaDaPessoa } from '@/nucleo/pessoaNatureza';

/**
 * O titular, do lado da tela.
 *
 * Titular e o PROPRIETARIO do veiculo — pessoa fisica ou juridica registrada no
 * documento. Ele nao e o credenciado: um credenciado opera veiculos de terceiros, e o
 * titular pode nao ter relacao nenhuma com o programa alem de ser dono do bem.
 *
 * ESTA E UMA TELA DE CONSULTA, NAO DE CADASTRO. Titular chega por integracao com o SGTP;
 * o TEG le e confere. E o que explica a forma da tela: nao ha acao de criar, o filtro
 * principal e a origem do dado, e a coluna que mais importa e a de sincronizacao.
 *
 * O CPF E O CNPJ SAO COLUNAS SEPARADAS, e nao um `cpf_cnpj` com o tipo ao lado. E assim
 * no banco, e a diferenca aparece aqui: `natureza` sai de qual das duas veio preenchida,
 * em vez de vir num campo que poderia contradizer o documento.
 */
export interface Titular extends CamposDeIntegracao {
  id: string;
  /** Identificador legivel do endereco de detalhe; o id, quando o cadastro nao tem slug. */
  slug: string;
  /**
   * Preenchido quando o titular e pessoa fisica. MASCARADO PELO BANCO
   * (`***.456.789-**`): o CPF completo nao sai da API.
   */
  cpf: string | null;
  /**
   * Preenchido quando o titular e pessoa juridica. Na lista e na exportacao chega
   * MASCARADO PELO BANCO (so os dois primeiros digitos, a filial e o verificador);
   * inteiro (so digitos) so no detalhe,
   * que registra o acesso.
   */
  cnpj: string | null;
  nome: string;
  status: SituacaoDaPessoa;
  /**
   * `true` quando o dado do TEG e o do SGTP discordam e ninguem decidiu qual vale.
   *
   * NAO E O MESMO QUE `em_contingencia`: contingencia e "a integracao estava fora e
   * cadastramos a mao"; conflito e "a integracao respondeu, e a resposta briga com o que
   * esta aqui". O segundo caso exige decisao humana registrada, e nao reconciliacao
   * automatica — por isso ele ganha selo proprio na coluna de sincronizacao.
   */
  conflito_integracao: boolean | null;
  created_at: string;
}

/** O titular no detalhe (`GET bff/v1/titulares/:referencia`). */
export interface TitularDetalhe extends Titular {
  /** So existe para pessoa fisica. */
  rg: string | null;
  /** So existe para pessoa fisica. ISO 8601 (`YYYY-MM-DD`). */
  data_nascimento: string | null;
  crmc_numero: string | null;
  /** ISO 8601 (`YYYY-MM-DD`). */
  crmc_validade: string | null;
  telefone: string | null;
  email: string | null;
  cep: string | null;
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  observacoes: string | null;
  detalhe_conflito: string | null;
  /** Identificador do registro no SGTP, quando `origem_dado` for `INTEGRACAO_SGTP`. */
  referencia_integracao: string | null;
  /** Quando um conflito foi resolvido por decisao humana registrada (`MANUAL_ASSISTIDO`). */
  decisao_assistida_em: string | null;
  updated_at: string;
  /** Veiculos cuja propriedade pertence, ou pertenceu, a este titular, no recorte do perfil. */
  veiculos: VinculoVeiculoDoTitular[];
  permissoes: PermissoesDeLeitura;
}

/** Um veiculo cuja propriedade pertence, ou pertenceu, a este titular. */
export interface VinculoVeiculoDoTitular {
  id: string;
  /** ISO 8601 (`YYYY-MM-DD`). */
  data_inicio: string | null;
  /** `null` enquanto o vinculo estiver vigente. */
  data_fim: string | null;
  tipo_vinculo: string | null;
  veiculo: {
    id: string;
    slug: string;
    placa: string | null;
    modelo: string | null;
  };
}

export interface FiltroDeTitulares {
  pagina: number;
  tamanho: number;
  busca?: string;
  situacao?: string;
  natureza?: NaturezaDaPessoa;
  origem?: OrigemDoDado;
}

/**
 * Pessoa fisica ou juridica, decidido por qual documento veio.
 *
 * Sem campo de tipo: o documento e a prova. Um `tipo_pessoa` gravado poderia dizer `PJ`
 * numa linha que so tem CPF, e ai a tela teria de escolher em quem acreditar.
 */
export function naturezaDoTitular(titular: Pick<Titular, 'cpf' | 'cnpj'>): NaturezaDaPessoa | null {
  if (titular.cpf) {
    return 'PF';
  }
  if (titular.cnpj) {
    return 'PJ';
  }
  return null;
}

/** O documento que identifica o titular: o CPF, ou o CNPJ quando nao houver CPF. */
export function documentoDoTitular(titular: Pick<Titular, 'cpf' | 'cnpj'>): string | null {
  return titular.cpf || titular.cnpj || null;
}

export const OPCOES_DE_NATUREZA: readonly { value: NaturezaDaPessoa; label: string }[] = [
  { value: 'PF', label: 'Pessoa física' },
  { value: 'PJ', label: 'Pessoa jurídica' },
];
